'use client';

import { Open } from '@velocity-exchange/icons';
import { cj } from 'src/utils/classNames';
import NumberDisplay from '../../../NumberDisplay/NumberDisplay';
import InlineLoadingBar from '../../../InlineLoadingBar/InlineLoadingBar';
import TableV2 from '../../../Tables/TableV2';
import { getUrlForAccount, getUrlForTx } from 'src/utils/explorer';
import { useOrderRowData } from './useOrderRowData';
import type { OrderRowCollapsedContentProps } from './types';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import DevRecordTooltip from './DevRecordTooltip';
import { PRESETS } from 'src/utils/formatNumber/presets';
import { formatText } from '@velocity-exchange/common/format';

export default function OrderRowCollapsedContentDesktop({
	orderId,
	displayDatesAsTimestamp,
	expanded,
	amountDisplayType,
	orderedQuoteValue,
}: OrderRowCollapsedContentProps) {
	const { processedRecords, isLoading, hasData } = useOrderRowData({
		orderId,
		expanded,
	});

	const devSwitchIsOn = useDevSwitchIsOn();

	if (!hasData) {
		if (isLoading) {
			return (
				<div className="flex h-full w-full items-center justify-center py-4">
					<InlineLoadingBar />
				</div>
			);
		}
		return null;
	}

	return (
		<div className="w-full rounded-sm border border-stroke-divider bg-container-bg p-md">
			{processedRecords.map((data, index) => (
				<DevRecordTooltip
					key={`${data.record.txSig}_${index}`}
					record={data.record}
					devSwitchIsOn={devSwitchIsOn}
				>
					<div
						className={cj(
							'flex w-full justify-between bg-container-bg',
							index !== processedRecords.length - 1 &&
								'border-b border-stroke-divider pb-lg',
							index !== 0 && 'pt-lg'
						)}
					>
						<div className="flex w-[18%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">Time</div>
							<TableV2.DateCell
								className="py-0 typo-label-xs text-text-default"
								displayAsTimestamp={displayDatesAsTimestamp}
								date={data.date}
							/>
						</div>

						<div className="flex w-[12%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">
								Avg. Fill Price
							</div>
							<div className="typo-label-xs text-text-default">
								{data.isNonFillAction
									? '-'
									: data.avgPriceBn.toNotional(
											undefined,
											undefined,
											data.priceDecimalOverride
										)}
							</div>
						</div>

						<div className="flex w-[10%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">
								{amountDisplayType === 'base' ? 'Order Size' : 'Order Value'}
							</div>
							<div className="typo-label-xs text-text-default">
								{data.orderedIsEntire ? (
									'Entire Position'
								) : amountDisplayType === 'base' ? (
									<NumberDisplay
										value={data.tradeInfo.baseAssetAmount}
										options={PRESETS.tradePrecisionTrim}
										isAccountValueToHide
									/>
								) : (
									(orderedQuoteValue ?? 'Market')
								)}
							</div>
						</div>

						<div className="flex w-[10%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">
								{amountDisplayType === 'base' ? 'Filled Size' : 'Filled Value'}
							</div>
							<div className="typo-label-xs text-text-default">
								{data.isNonFillAction ? (
									'-'
								) : amountDisplayType === 'base' ? (
									<NumberDisplay
										value={data.tradeInfo.baseAssetAmountFilled}
										options={PRESETS.tradePrecisionTrim}
										isAccountValueToHide
									/>
								) : (
									formatText(data.tradeInfo.quoteAssetAmountFilled, PRESETS.usd)
								)}
							</div>
						</div>

						<div className="flex w-[15%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">
								{amountDisplayType === 'base'
									? 'Cumulative Size'
									: 'Cumulative Value'}
							</div>
							<div className="typo-label-xs text-text-default">
								{data.isNonFillAction ? (
									'-'
								) : amountDisplayType === 'base' ? (
									<NumberDisplay
										value={data.tradeInfo.totalBaseAmountFilled}
										options={PRESETS.tradePrecisionTrim}
										isAccountValueToHide
									/>
								) : (
									formatText(data.tradeInfo.totalQuoteAmountFilled, PRESETS.usd)
								)}
							</div>
						</div>

						<div className="flex w-[10%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">Fee</div>
							<div className="typo-label-xs text-text-default">
								{data.isNonFillAction || !data.tradeInfo.fee ? (
									'-'
								) : (
									<NumberDisplay
										value={data.tradeInfo.fee}
										options={PRESETS.usd}
									/>
								)}
							</div>
						</div>

						<div className="flex w-[12%] flex-col gap-y-xs">
							<div className="typo-body-xs text-text-secondary">Filled by</div>
							<div className="typo-label-xs text-text-default">
								{data.isNonFillAction ? (
									'-'
								) : data.isDefaultCounterparty ? (
									data.filledBy
								) : (
									<a
										href={
											data.counterpartyStr
												? getUrlForAccount(data.counterpartyStr)
												: '#'
										}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
									>
										{data.filledBy}
									</a>
								)}
							</div>
						</div>

						<div className="flex w-[13%] flex-col items-end gap-y-xs">
							<div className="typo-body-xs text-text-secondary">Status</div>
							<div className="flex items-center gap-1 typo-label-xs whitespace-nowrap text-text-default">
								{data.status === 'Partially Filled (Cancelled)'
									? 'Cancelled'
									: data.status}
								{data.record.txSig && (
									<a
										href={getUrlForTx(data.record.txSig)}
										target="_blank"
										rel="noopener noreferrer"
										className="flex"
										onClick={(e) => e.stopPropagation()}
									>
										<Open autoSize />
									</a>
								)}
							</div>
						</div>
					</div>
				</DevRecordTooltip>
			))}
		</div>
	);
}
